import { useMemo } from "react";
import useUserProfileStore from "../zustandStores/userProfileStore";
import { get50PerUsersCheck } from "../services/utils";
import AB_TEST_CONFIG from "../config/abTestConfig";

/**
 * Custom hook for A/B testing
 *
 * @param testName - The name of the A/B test from AB_TEST_CONFIG
 * @returns Object containing A/B test state and configuration
 */
export const useABTest = (testName: string) => {
  const { profileData } = useUserProfileStore();

  const result = useMemo(() => {
    const recruiterId = profileData?.recruiter_id;
    const config = AB_TEST_CONFIG[testName];

    // If test is not configured or disabled, return control group
    if (!config || !config.enabled) {
      return {
        isVariant: false,
        recruiterId,
        config: config || null
      };
    }

    // Determine if user should see variant based on recruiter ID
    const isVariant = get50PerUsersCheck(recruiterId) || false;

    return {
      isVariant,
      recruiterId,
      config
    };
  }, [profileData?.recruiter_id, testName]);

  return result;
};

/**
 * Specific hook for Contact Sales A/B test
 * Provides a more semantic API for this specific test
 */
export const useContactSalesABTest = () => {
  const abTest = useABTest("CONTACT_SALES");
  return {
    shouldShowV2Form: abTest.isVariant,
    recruiterId: abTest.recruiterId,
    config: abTest.config
  };
};

/**
 * Utility function to check if a specific A/B test variant should be shown
 * Can be used in non-React contexts or for simple boolean checks
 *
 * @param testName - The name of the A/B test from AB_TEST_CONFIG
 * @param recruiterId - The recruiter ID to check against
 * @returns boolean indicating if variant should be shown
 */
export const isABTestVariant = (
  testName: string,
  recruiterId?: number | string
): boolean => {
  const config = AB_TEST_CONFIG[testName];

  if (!config || !config.enabled) {
    return false;
  }

  return get50PerUsersCheck(recruiterId) || false;
};

export default useABTest;
